import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import AnalysisResults from './AnalysisResults';
import { supabase } from '../lib/supabase';

const metricsSchema = z.object({
  leadsPerMonth: z.number().min(0, 'Valor deve ser maior ou igual a 0'),
  newClientsPerMonth: z.number().min(0, 'Valor deve ser maior ou igual a 0'),
  averageProjectValue: z.number().min(0, 'Valor deve ser maior ou igual a 0'),
  marketingInvestment: z.number().min(0, 'Valor deve ser maior ou igual a 0'),
  acquisitionCosts: z.number().min(0, 'Valor deve ser maior ou igual a 0'),
  acquisitionChannels: z.array(z.string()).min(1, 'Selecione pelo menos um canal'),
});

type MetricsData = z.infer<typeof metricsSchema>;

const acquisitionChannelOptions = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'google', label: 'Google meu Negócio' },
  { id: 'referrals', label: 'Indicações' },
  { id: 'paid', label: 'Tráfego Pago' },
  { id: 'portals', label: 'Portais de Arquitetura' },
  { id: 'automation', label: 'Automação' },
  { id: 'website', label: 'Site' },
  { id: 'others', label: 'Outros' },
];

const formatCurrency = (value: number) => {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCurrencyInput = (value: string | undefined | null): number => {
  if (!value) return 0;
  return Number(value.replace(/\D/g, '')) / 100;
};

const formatCurrencyInput = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  const floatValue = Number(numericValue) / 100;
  return floatValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function MetricsForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<any | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MetricsData>({
    resolver: zodResolver(metricsSchema),
    defaultValues: {
      leadsPerMonth: 0,
      newClientsPerMonth: 0,
      averageProjectValue: 0,
      marketingInvestment: 0,
      acquisitionCosts: 0,
      acquisitionChannels: [],
    },
  });

  const leadsPerMonth = Number(watch('leadsPerMonth')) || 0;
  const newClientsPerMonth = Number(watch('newClientsPerMonth')) || 0;
  const conversionRate = leadsPerMonth > 0 
    ? ((newClientsPerMonth / leadsPerMonth) * 100) 
    : 0;

  const onSubmit = async (data: MetricsData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Calculate additional metrics
      const totalProjects = data.newClientsPerMonth;
      const totalProjectsValue = data.averageProjectValue * totalProjects;
      const totalAcquisitionCosts = data.acquisitionCosts;
      const calculatedConversionRate = data.leadsPerMonth > 0 
        ? (data.newClientsPerMonth / data.leadsPerMonth) * 100 
        : 0;

      // Update the latest lead record with metrics data
      const { data: leads, error: fetchError } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;

      if (leads && leads.length > 0) {
        const latestLead = leads[0];
        const { error: updateError } = await supabase
          .from('leads')
          .update({
            leads_per_month: data.leadsPerMonth,
            new_clients_per_month: data.newClientsPerMonth,
            average_project_value: data.averageProjectValue,
            marketing_investment: data.marketingInvestment,
            total_acquisition_costs: data.acquisitionCosts
          })
          .eq('id', latestLead.id);

        if (updateError) throw updateError;
      }

      // Prepare complete analysis data
      const completeData = {
        ...data,
        conversionRate: calculatedConversionRate,
        totalProjects,
        totalProjectsValue,
        totalAcquisitionCosts,
      };
      
      setAnalysisData(completeData);
    } catch (error) {
      console.error('Error submitting metrics:', error);
      setSubmitError('Ocorreu um erro ao enviar os dados. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (analysisData) {
    return <AnalysisResults formData={analysisData} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="mb-8">
          <a
            href="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar para página inicial
          </a>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Análise de Métricas
          </h1>
          <p className="text-gray-600 text-center mb-8">
            Preencha os dados abaixo para receber sua análise personalizada
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantidade de leads por mês
              </label>
              <input
                type="number"
                {...register('leadsPerMonth', { valueAsNumber: true })}
                className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
              {errors.leadsPerMonth && (
                <p className="mt-1 text-sm text-red-600">{errors.leadsPerMonth.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Novos clientes no mês
              </label>
              <input
                type="number"
                {...register('newClientsPerMonth', { valueAsNumber: true })}
                className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
              {errors.newClientsPerMonth && (
                <p className="mt-1 text-sm text-red-600">{errors.newClientsPerMonth.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-600">
                Taxa de conversão: {conversionRate.toFixed(1)}%
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor médio do projeto
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500">R$</span>
                </div>
                <input
                  type="text"
                  {...register('averageProjectValue', {
                    setValueAs: parseCurrencyInput,
                    onChange: (e) => {
                      e.target.value = formatCurrencyInput(e.target.value);
                    }
                  })}
                  className="block w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0,00"
                />
              </div>
              {errors.averageProjectValue && (
                <p className="mt-1 text-sm text-red-600">{errors.averageProjectValue.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Investimento em marketing
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500">R$</span>
                </div>
                <input
                  type="text"
                  {...register('marketingInvestment', {
                    setValueAs: parseCurrencyInput,
                    onChange: (e) => {
                      e.target.value = formatCurrencyInput(e.target.value);
                    }
                  })}
                  className="block w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0,00"
                />
              </div>
              {errors.marketingInvestment && (
                <p className="mt-1 text-sm text-red-600">{errors.marketingInvestment.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custos totais de aquisição (marketing, vendas, comissões)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500">R$</span>
                </div>
                <input
                  type="text"
                  {...register('acquisitionCosts', {
                    setValueAs: parseCurrencyInput,
                    onChange: (e) => {
                      e.target.value = formatCurrencyInput(e.target.value);
                    }
                  })}
                  className="block w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0,00"
                />
              </div>
              {errors.acquisitionCosts && (
                <p className="mt-1 text-sm text-red-600">{errors.acquisitionCosts.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Canais de aquisição
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {acquisitionChannelOptions.map((channel) => (
                  <label
                    key={channel.id}
                    className="relative flex items-center justify-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      {...register('acquisitionChannels')}
                      value={channel.id}
                      className="sr-only peer"
                    />
                    <span className="text-sm text-center">{channel.label}</span>
                    <div className="absolute inset-0 rounded-lg ring-2 ring-transparent peer-checked:ring-blue-500 pointer-events-none" />
                  </label>
                ))}
              </div>
              {errors.acquisitionChannels && (
                <p className="mt-1 text-sm text-red-600">{errors.acquisitionChannels.message}</p>
              )}
            </div>

            {submitError && (
              <div className="rounded-lg bg-red-50 p-4">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}

            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analisando...
                  </span>
                ) : (
                  'Analisar Métricas'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}